import React, { useState, useEffect, useCallback } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { Radio, Heart, CalendarClock, MessageSquareDot, Music4, Megaphone, LogIn, LogOut, CalendarDays, HandHeart, Menu, Mic, BookOpen, Users, CalendarCheck, Shield, User, Church, ExternalLink, Flame, Crown, X } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import BottomPlayer from "@/components/BottomPlayer";
import LiveBanner from "@/components/LiveBanner";
import ScriptureDialog from "@/components/ScriptureDialog";
import NotificationBell from "@/components/NotificationBell";
import InstallButton from "@/components/InstallButton";
import ShareApp from "@/components/ShareApp";
import Footer from "@/components/Footer";
import OnboardingTutorial from "@/components/OnboardingTutorial";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const mobileNav = [
  { to: "/", label: "Listen", icon: Radio },
  { to: "/favorites", label: "Faves", icon: Heart },
  { to: "/events", label: "Events", icon: CalendarDays },
  { to: "/chat", label: "Chat", icon: MessageSquareDot },
];

export default function AppLayout() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#0A1030] flex">
      {user && <Sidebar />}
      <div className="flex-1 min-w-0 flex flex-col overflow-x-hidden">
        <LiveBanner />
        <CallerOfDayBanner />
        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3 px-4 sm:px-6 lg:px-12 pt-4" data-testid="topbar">
          <a
            href="https://ihimm.org/give"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="topbar-donate"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#FF3B30]/10 text-[#FF7A70] hover:bg-[#FF3B30]/20 text-xs sm:text-sm font-bold transition-colors shrink-0"
          >
            <HandHeart className="w-4 h-4" /> Give
          </a>
          <InstallButton className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#E6B34A]/10 text-[#E6B34A] text-xs font-bold shrink-0" />
          <ShareApp className="hidden sm:inline-flex lg:hidden items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 text-zinc-200 text-xs font-semibold shrink-0" />
          {user ? (
            <>
              <StreakBadge />
              <NotificationBell />
            </>
          ) : (
            <Link to="/login" data-testid="topbar-signin" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] text-sm font-bold hover:bg-[#CF9A34] transition-colors shrink-0">
              <LogIn className="w-4 h-4" /> Sign in
            </Link>
          )}
        </div>
        <main className="flex-1 pb-40">
          <Outlet />
          <Footer />
        </main>
      </div>
      <BottomPlayer />
      {user && <MobileNav />}
      <ScriptureDialog />
      {user && <OnboardingTutorial />}
    </div>
  );
}

function CallerOfDayBanner() {
  const [name, setName] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    let alive = true;
    const f = () => api.get("/public/caller-of-the-day").then((r) => { if (alive) setName(r.data?.name || null); }).catch(() => {});
    f();
    const t = setInterval(f, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  useEffect(() => { setDismissed(false); }, [name]);
  if (!name || dismissed) return null;
  return (
    <div data-testid="cotd-banner" className="mx-4 sm:mx-6 lg:mx-12 mt-3 flex items-center gap-2.5 rounded-xl border border-[#E6B34A]/50 bg-gradient-to-r from-[#E6B34A]/20 to-transparent px-4 py-2.5">
      <Crown className="w-4 h-4 text-[#E6B34A] shrink-0" fill="currentColor" />
      <p className="text-sm text-white flex-1 min-w-0 truncate"><span className="font-bold text-[#E6B34A]">Caller of the Day:</span> {name} 🎉</p>
      <button onClick={() => setDismissed(true)} data-testid="cotd-banner-close" className="shrink-0 text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
    </div>
  );
}

function StreakBadge() {
  const [streak, setStreak] = useState(null);
  useEffect(() => {
    let alive = true;
    const fetchStreak = () => api.get("/me/streak").then((r) => { if (alive) setStreak(r.data); }).catch(() => {});
    fetchStreak();
    const t = setInterval(fetchStreak, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (!streak || !streak.current) return null;
  return (
    <div
      data-testid="streak-badge"
      title={`${streak.current}-day listening streak${streak.longest > streak.current ? ` · best ${streak.longest}` : ""}`}
      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#FF7A18]/10 text-[#FF9A4A] text-xs sm:text-sm font-bold shrink-0"
    >
      <Flame className="w-4 h-4" fill="currentColor" />
      <span>{streak.current}</span>
      <span className="hidden sm:inline font-semibold text-[#FF9A4A]/80">day{streak.current === 1 ? "" : "s"}</span>
    </div>
  );
}

function MobileNav() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [counts, setCounts] = useState({ chat: 0, prayer_wall: 0 });
  const [seen, setSeen] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nav_seen") || "{}"); } catch { return {}; }
  });

  const markSeen = useCallback((key, value) => {
    setSeen((prev) => {
      if (prev[key] === value) return prev;
      const next = { ...prev, [key]: value };
      localStorage.setItem("nav_seen", JSON.stringify(next));
      return next;
    });
  }, []);

  useEffect(() => {
    let alive = true;
    const poll = () => api.get("/activity/counts").then((r) => { if (alive) setCounts(r.data || { chat: 0, prayer_wall: 0 }); }).catch(() => {});
    poll();
    const t = setInterval(poll, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // When the user lands on a section, clear its badge to the current count.
  useEffect(() => {
    if (location.pathname === "/chat") markSeen("chat", counts.chat);
    if (location.pathname === "/prayer-wall") markSeen("prayer_wall", counts.prayer_wall);
  }, [location.pathname, counts.chat, counts.prayer_wall, markSeen]);

  const unread = (key) => Math.max(0, (counts[key] || 0) - (seen[key] || 0));
  const unreadChat = unread("chat");
  const unreadPrayer = unread("prayer_wall");
  const moreHasNew = unreadPrayer > 0;

  const linkCls = ({ isActive }) =>
    `relative flex flex-col items-center gap-1 text-[10px] font-semibold ${isActive ? "text-[#E6B34A]" : "text-zinc-500"}`;

  const moreItems = [
    { to: "/live", label: "Gospel Show", icon: Mic },
    { to: "/schedule", label: "Schedule", icon: CalendarClock },
    { to: "/requests", label: "Requests", icon: Music4 },
    { to: "/bible", label: "Bible", icon: BookOpen },
    ...(user ? [{ to: "/prayer", label: "Prayer Requests", icon: HandHeart }] : []),
    { to: "/prayer-wall", label: "Prayer Wall", icon: Users, badge: unreadPrayer },
    { to: "/advertise", label: "Advertise", icon: Megaphone },
    ...(user ? [{ to: "/my-events", label: "My Events", icon: CalendarCheck }] : []),
    ...(user ? [{ to: "/my-spots", label: "My Spots", icon: Megaphone }] : []),
    ...(user && (user.role === "host" || user.role === "admin") ? [{ to: "/host", label: "Host Studio", icon: Mic }] : []),
    ...(user && user.role === "admin" ? [{ to: "/admin", label: "Admin", icon: Shield }] : []),
    ...(user ? [{ to: "/profile", label: "My Profile", icon: User }] : []),
  ];

  const sheetLinkCls = ({ isActive }) =>
    `relative flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
      isActive ? "bg-[#E6B34A] text-[#0A1030]" : "text-zinc-300 hover:text-white hover:bg-white/5"
    }`;

  const Badge = ({ count, className = "" }) => count > 0 ? (
    <span className={`min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-black flex items-center justify-center leading-none ${className}`} data-testid="nav-badge">
      {count > 99 ? "99+" : count}
    </span>
  ) : null;

  return (
    <>
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 glass border-t flex justify-around py-2 px-2">
        {mobileNav.map((n) => (
          <NavLink key={n.to} to={n.to} className={linkCls} end={n.to === "/"} data-testid={`mnav-${n.label.toLowerCase()}`}>
            <span className="relative">
              <n.icon className="w-5 h-5" />
              {n.to === "/chat" && <Badge count={unreadChat} className="absolute -top-2 -right-2.5" />}
            </span>
            {n.label}
          </NavLink>
        ))}
        <button onClick={() => setMoreOpen(true)} className="relative flex flex-col items-center gap-1 text-[10px] font-semibold text-zinc-500" data-testid="mnav-more">
          <span className="relative">
            <Menu className="w-5 h-5" />
            {moreHasNew && <span className="absolute -top-1.5 -right-2 w-2.5 h-2.5 rounded-full bg-[#FF3B30] ring-2 ring-[#0A1030]" data-testid="more-new-dot" />}
          </span>
          More
        </button>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="lg:hidden z-[70] bg-[#0C1338] border-t border-white/10 rounded-t-2xl max-h-[85vh] overflow-y-auto p-0" data-testid="mobile-more-sheet">
          <SheetHeader className="px-6 pt-6 pb-2 text-left">
            <SheetTitle className="text-white font-display font-extrabold text-lg">Menu</SheetTitle>
          </SheetHeader>
          <div className="px-4 pb-24 flex flex-col gap-1.5">
            <a
              href="https://ihimm.org/give"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setMoreOpen(false)}
              data-testid="more-give"
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-[#0A1030] bg-[#FF3B30] hover:bg-[#e6352b] transition-colors"
            >
              <HandHeart className="w-5 h-5 shrink-0" /> Give — Support the Ministry
            </a>
            <div onClick={() => setMoreOpen(false)} className="flex flex-col gap-1.5">
              {moreItems.map((n) => (
                <NavLink key={n.to} to={n.to} className={sheetLinkCls} data-testid={`more-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <n.icon className="w-5 h-5 shrink-0" />
                  <span className="flex-1">{n.label}</span>
                  <Badge count={n.badge || 0} />
                </NavLink>
              ))}
              <a
                href="https://ihimm.org"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="more-ministry"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-zinc-200 bg-[#E6B34A]/10 border border-[#E6B34A]/30 hover:bg-[#E6B34A]/20 transition-colors mt-1"
              >
                <Church className="w-5 h-5 shrink-0 text-[#E6B34A]" />
                <span className="flex-1 leading-tight">
                  In HIS Image Movement Ministries
                  <span className="block text-[10px] font-bold uppercase tracking-wide text-[#E6B34A] mt-0.5">Parent Ministry</span>
                </span>
                <ExternalLink className="w-3.5 h-3.5 opacity-70" />
              </a>
              {user && (
                <button
                  onClick={logout}
                  data-testid="more-logout"
                  className="flex items-center justify-center gap-2 px-4 py-3 mt-2 rounded-xl bg-white/5 hover:bg-white/10 text-sm font-semibold text-white transition-colors"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
